// @ts-nocheck
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, IconButton, Typography } from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import VisibilityIcon from '@mui/icons-material/Visibility';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';

import useDocumentAttributeNavigation from '../../../../hooks/useDocumentAttributeNavigation';

const ENTITY_TYPES_WITH_DOCUMENT_TAB = ['modelRequest', 'useCase', 'query'];

const DocumentActionButton = ({
  documentId,
  documentLocation,
  inventoryType,
}) => {
  const navigate = useNavigate();
  const documentAttributeNavigation = useDocumentAttributeNavigation();
  const location = useLocation();
  const { state: breadcrumbDetails } = location;
  const { frontendConfig } = useSelector((state) => state?.tenant);

  //for temporal view
  let path = '';
  if (documentLocation) {
    path = `${location?.pathname}/${documentLocation}?tab=2&docTab=2&searchQuery=${documentId}`;
  } else if (ENTITY_TYPES_WITH_DOCUMENT_TAB.includes(inventoryType)) {
    path = `${location?.pathname}?tab=document&docTab=2&searchQuery=${documentId}`;
  } else {
    path = `${location?.pathname}?tab=2&docTab=2&searchQuery=${documentId}`;
  }
  const handleGoToDocument = useCallback(() => {
    if (documentAttributeNavigation?.openDocumentTab) {
      documentAttributeNavigation?.openDocumentTab({ documentId });
      return;
    }

    navigate(path, {
      state: breadcrumbDetails,
    });
  }, [
    breadcrumbDetails,
    documentAttributeNavigation,
    documentId,
    navigate,
    path,
  ]);

  const ActionIcon = useMemo(
    () =>
      frontendConfig?.documentTypeAttributeRedirectIcon
        ? OpenInNewIcon
        : VisibilityIcon,
    [frontendConfig?.documentTypeAttributeRedirectIcon]
  );

  return (
    <IconButton
      size="small"
      title="Go to document"
      onClick={handleGoToDocument}
    >
      <ActionIcon color="primary" />
    </IconButton>
  );
};

DocumentActionButton.propTypes = {
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  documentLocation: PropTypes.string,
  inventoryType: PropTypes.string,
};

const ViewDocumentAttribute = ({
  documentDetails,
  inventoryType,
  isViewMode = false,
  configColumn = false,
}) => {
  // Handle both single object and array cases
  const documents = Array.isArray(documentDetails)
    ? documentDetails
    : [documentDetails];

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      {documents?.map((document, index) => (
        <Box
          key={document?.document_id || index}
          display="flex"
          alignItems="center"
          gap={1}
          width="100%"
        >
          <ArticleIcon
            color="primary"
            sx={{ width: 20, height: 20, flexShrink: 0 }}
          />
          <Typography
            color="primary"
            sx={{
              flex: '1 1 0',
              minWidth: 0,
              ...(configColumn
                ? {
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                  }
                : {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }),
            }}
            title={`${document?.document_name}.${document?.document_type}`}
          >
            {`${document?.document_name}.${document?.document_type}`}
          </Typography>
          {((!configColumn && !isViewMode) || (configColumn && isViewMode)) && (
            <Box sx={{ flexShrink: 0 }}>
              <DocumentActionButton
                documentId={document?.document_id}
                documentLocation={document?.unique_id}
                inventoryType={inventoryType}
              />
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};

ViewDocumentAttribute.propTypes = {
  documentDetails: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        document_name: PropTypes.string.isRequired,
        document_type: PropTypes.string.isRequired,
        document_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
      })
    ),
    PropTypes.shape({
      document_name: PropTypes.string.isRequired,
      document_type: PropTypes.string.isRequired,
      document_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    }),
  ]).isRequired,
  inventoryType: PropTypes.string,
  isViewMode: PropTypes.bool,
  configColumn: PropTypes.bool,
};

export default ViewDocumentAttribute;
